import { api } from "@Aspecto/backend/convex/_generated/api";
import { useMutation } from "convex/react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { Switch } from "@/components/ui/switch";
import { useCurrentCompany } from "@/hooks/use-current-account";

export const Route = createFileRoute("/_authenticated/app/admin/setup")({
  component: SetupSettings,
});

function SetupSettings() {
  const { data: company } = useCurrentCompany();
  const updateGlobalCommunityEnabled = useMutation(api.companies.updateGlobalCommunityEnabled);
  const updateLeadsSpaceEnabled = useMutation(api.companies.updateLeadsSpaceEnabled);
  const updateAllowMembersToPost = useMutation(api.companies.updateAllowMembersToPost);
  const updateAllowMembersToComment = useMutation(api.companies.updateAllowMembersToComment);
  const updateAllowLikes = useMutation(api.companies.updateAllowLikes);

  const toggle = async (
    mutationFn: (args: { enabled: boolean }) => Promise<unknown>,
    enabled: boolean,
  ) => {
    try {
      await mutationFn({ enabled });
    } catch {
      toast.error("Failed to save. Please try again.");
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard title="Spaces" description="Enable / disable special spaces.">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="globalCommunity">General space</Label>
            <p className="text-xs text-muted-foreground">
              A space where everyone is automatically a member.
            </p>
          </div>
          <Switch
            id="globalCommunity"
            checked={company?.globalCommunityEnabled !== false}
            onCheckedChange={(checked) => toggle(updateGlobalCommunityEnabled, checked)}
          />
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="leadsSpace">Leads space</Label>
            <p className="text-xs text-muted-foreground">
              A space where all leads are automatically members.
            </p>
          </div>
          <Switch
            id="leadsSpace"
            checked={company?.leadsSpaceEnabled !== false}
            onCheckedChange={(checked) => toggle(updateLeadsSpaceEnabled, checked)}
          />
        </div>
      </SectionCard>

      <SectionCard title="Interaction" description="Control the level of member interaction">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="allowMembersToPost">Updates</Label>
            <p className="text-xs text-muted-foreground">
              Allow leads to enable their members to share updates
            </p>
          </div>
          <Switch
            id="allowMembersToPost"
            checked={company?.allowMembersToPost === true}
            onCheckedChange={(checked) => toggle(updateAllowMembersToPost, checked)}
          />
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="allowMembersToComment">Commenting</Label>
            <p className="text-xs text-muted-foreground">
              Allow leads to enable their members to comment
            </p>
          </div>
          <Switch
            id="allowMembersToComment"
            checked={company?.allowMembersToComment === true}
            onCheckedChange={(checked) => toggle(updateAllowMembersToComment, checked)}
          />
        </div>
        <div className="mt-6 flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="allowLikes">Likes</Label>
            <p className="text-xs text-muted-foreground">
              Enable likes for leads, and allow them to enable it for their members
            </p>
          </div>
          <Switch
            id="allowLikes"
            checked={company?.allowLikes === true}
            onCheckedChange={(checked) => toggle(updateAllowLikes, checked)}
          />
        </div>
      </SectionCard>
    </div>
  );
}
